import { ESdChangeReason, ISessionVariable } from "@kuika/kuika-cl-model";
import ObjectHash from "object-hash";
import * as React from "react";
import { connect } from "react-redux";
import { DatasourceService } from "../../../services/datasource-service";
import { IWithSDProps, withSD } from "../../../shared/hoc/screen-design-consumer";
import { PureSdComponent } from "../../../shared/pure-sd";
import { KMainFunctions } from "../../../shared/utility/main-functions";
import { SaveStatus } from "../../../shared/utility/save-status";
import { AppState } from "../../../store/root-reducer";
import { setNextCallbackFunction, setShowModal } from "../../../store/unsaved-changes-modal/actions";
import { runParkedNavigation } from "../../../store/unsaved-changes-modal/run-parked-navigation";
import Trash12RedSvg from "../../../svgs/small-icons/trash-12-red.svg";
import CloseConfirmationModal from "../../common/close-confirmation-modal";
import { type KuikaButtonStatus } from "../../ui-kit/kuika-button/kuika-button";
import KuikaEditorHeader from "../../ui-kit/kuika-editor-header/kuika-editor-header";
import KuikaInput from "../../ui-kit/kuika-input/kuika-input";
import { KuikaEditorIdentity } from "../../ui-kit/kuika-page-header/kuika-editor-title";
import KuikaSpinnerSmall from "../../ui-kit/kuika-spinner-small/kuika-spinner-small";
import KuikaSwitch from "../../ui-kit/kuika-switch/kuika-switch";
import KuikaTable, { KuikaTableColumn } from "../../ui-kit/kuika-table/kuika-table";
import { getEditorGlyph, IEditorGlyph } from "./editor-switcher/editor-glyph";
import { EditorGlyphIcon } from "./editor-switcher/editor-glyph-icon";
import "./create-session-variable.scss";
import "./editor-bar-surface.scss";
import "./create-table.scss";

export interface ICreateSessionVariableState {
  /** Where the save stands. Owned by `SaveStatus`, never set directly. */
  saveStatus: KuikaButtonStatus;
  rows: ISessionVariableRow[];
  submitButtonDisabledStatus: boolean;
  /** Row ids whose key the list refused — outlined in place so they can be corrected rather than retyped. */
  invalidKeyRowIds: string[];
}

/**
 * A row on screen. `rowId` is what the table keys rows by, so it must outlive everything the user types: keying by
 * the variable's own key would re-key the row on each keystroke and remount the input mid-word.
 *
 * There is no separate "draft". The last row is simply the empty one, and a new empty row is appended the moment it
 * stops being empty — so the row being written keeps its identity, its cell and the caret inside it.
 */
interface ISessionVariableRow extends ISessionVariable {
  rowId: string;
}

let rowIdCounter = 0;
/** The key glyph the Sources tree gives the same row, in the database family's colour. */
const SESSION_VARIABLES_GLYPH: IEditorGlyph = getEditorGlyph({ kind: "sessionVariables" });

const blankRow = (): ISessionVariableRow => {
  rowIdCounter += 1;
  return { rowId: `session-variable-${rowIdCounter}`, key: "", value: "", isReadOnly: false };
};

/**
 * A row nobody has written a variable into: it is the place to write the next one, not a variable.
 *
 * Read Only is deliberately not part of this. It used to be, and flipping the switch on the trailing row was enough
 * to make the row count — it was then collected and saved as `{ key: "", value: "", isReadOnly: true }`.
 */
const isBlankRow = (row: ISessionVariableRow): boolean => !row.key.trim() && !row.value.trim();

/**
 * A variable cannot be built without both, so both are what makes the row one — the same two-field rule the table
 * editor's column grid uses. A key on its own is a half-typed thought, and growing the list there would put an
 * empty row under every first keystroke.
 */
const isCompleteRow = (row: ISessionVariableRow): boolean => !!row.key.trim() && !!row.value.trim();

/**
 * How long the last row is left alone before the empty one appears under it. It is a pause in typing, not a deadline:
 * leaving the row or pressing Enter grows the list at once. Long enough to sit inside a word — nobody stops for 600ms
 * between two letters of the same value — and short enough that the row is there by the time a hand reaches for it.
 */
const GROW_AFTER_TYPING_STOPS_MS: number = 600;

export interface ICreateSessionVariableProps {
  showModal: boolean;
  nextCallback?: () => void;
  setShowModal: (boolean) => void;
  setNextCallbackFunction: (cb?: () => void) => void;
}

class CreateSessionVariable extends PureSdComponent<
  ICreateSessionVariableProps & IWithSDProps,
  ICreateSessionVariableState
> {
  /** Scopes the draft-cell lookup to this table. */
  tableRef = React.createRef<HTMLDivElement>();

  objectHash: string = "";

  private readonly saveStatus: SaveStatus = new SaveStatus((saveStatus: KuikaButtonStatus): void => {
    this.setState({ saveStatus });
  });

  constructor(props: ICreateSessionVariableProps & IWithSDProps) {
    super(props);
    this.state = {
      saveStatus: "idle",
      submitButtonDisabledStatus: true,
      rows: [blankRow()],
      invalidKeyRowIds: []
    };
  }

  componentDidMount() {
    this.getSessionVariables();
  }

  componentWillUnmount(): void {
    this.saveStatus.dispose();
    this.clearGrowTimer();
  }

  componentDidUpdate(prevProps: ICreateSessionVariableProps & IWithSDProps) {
    // A save the pending navigation asked for ("Update & close") is still running or showing
    // its ✓: its own close runs the navigation. Asking again now would re-open the confirmation.
    if (this.props.nextCallback && this.saveStatus.canStart) {
      this.handleClose();
    }
  }

  getSessionVariables = async () => {
    const response = await DatasourceService.GetSessionVariables();
    const sessionVariables: ISessionVariable[] = response.data;

    if (sessionVariables) {
      const loaded: ISessionVariableRow[] = sessionVariables.map(({ key, value, isReadOnly }) => ({
        ...blankRow(),
        key,
        value,
        isReadOnly
      }));

      // Always one empty row at the end: it is where the next variable is written.
      this.setState({ rows: [...loaded, blankRow()] });
    }
  };

  onCreate = async () => {
    // The same rules the cells check on blur, re-run over everything about to be sent: a blur can be skipped
    // entirely (type, then click SAVE), and until this the list went to the server exactly as typed — a key with a
    // space, or two rows sharing one, which the endpoint resolves by letting the second overwrite the first.
    const offenders: ISessionVariableRow[] = this.state.rows
      .filter((row: ISessionVariableRow) => !isBlankRow(row))
      .filter((row: ISessionVariableRow) => this.validateKey(row.key, row.rowId) !== null);
    if (offenders.length > 0) {
      // Says why the save did not happen, which the cell's own blur message does not — and it has to be said:
      // a row that was left invalid earlier is only outlined, so refusing in silence would read as a dead button.
      this.setState({ invalidKeyRowIds: offenders.map((row: ISessionVariableRow) => row.rowId) });
      KMainFunctions.displayWarningNotification(
        offenders.length === 1 ? "Fix the highlighted key before saving" : "Fix the highlighted keys before saving"
      );
      return;
    }

    // The submit button is live again as soon as the ✓ replaces the spinner, so a second Enter
    // would re-fire the save behind the closing editor.
    if (!this.saveStatus.canStart) return;
    this.saveStatus.start();
    try {
      const response = await DatasourceService.UpdateSessionVariables(this.collectVariables());
      if (response.status !== 200) {
        // A resolved response that is not 200: the `finally` reset used to rescue this branch.
        this.saveStatus.fail();
        return;
      }
      // A close confirmation this save answered ("Update & close") steps aside at once, so the ✓ on
      // the button is what is seen before the editor leaves.
      this.props.setShowModal(false);
      // The ✓ goes up first and the editor leaves after it — see `SaveStatus`. Through the
      // close path, so a navigation that asked for this save ("Update & close") is carried out.
      this.saveStatus.succeed((): void => {
        this.close();
      });
    } catch (error) {
      this.saveStatus.fail();
      KMainFunctions.exceptionHandler(error);
    }
  };

  /** What the rows amount to: the variables, without the row ids the table needs and the trailing empty row. */
  collectVariables = (): ISessionVariable[] =>
    this.state.rows
      .filter((row: ISessionVariableRow) => !isBlankRow(row))
      .map(({ key, value, isReadOnly }: ISessionVariableRow) => ({ key: key.trim(), value, isReadOnly }));

  getObjectHash(state?: ICreateSessionVariableState, formValues?: any): string {
    const rows: ISessionVariableRow[] = (state ?? this.state).rows;
    return ObjectHash({
      sessionVariables: rows
        .filter((row: ISessionVariableRow) => !isBlankRow(row))
        .map(({ key, value, isReadOnly }: ISessionVariableRow) => ({ key: key.trim(), value, isReadOnly }))
    });
  }

  close() {
    if (!runParkedNavigation(this.props.nextCallback, this.props.setNextCallbackFunction)) {
      this.props.sd?.setEditingSessionVariables(false);
    }
  }

  handleClose() {
    if (this.objectHash === this.getObjectHash()) {
      this.close();
      return;
    }

    this.props.setShowModal(true);
  }

  /** The empty row at the end is where the next variable goes, so Enter hands the caret to it. */
  focusLastRowKey = () => {
    const rows: ISessionVariableRow[] = this.state.rows;
    const last: ISessionVariableRow | undefined = rows[rows.length - 1];
    if (!last) return;
    this.focusCell(last.rowId, "Key");
  };

  focusCell = (rowId: string, column: "Key" | "Value") => {
    // A row's identity for keyboard navigation is its `rowId`, not its key/value — the row being
    // typed has neither yet. `data-testid` is reserved for the E2E-facing ids below (which change
    // meaning as a row moves from draft to committed), so navigation gets its own stable attribute.
    const input = this.tableRef.current?.querySelector(`[data-nav-id="${rowId}${column}"]`);
    if (input instanceof HTMLInputElement) input.focus();
  };

  /** The rules the compose strip's form carried: required, no spaces, unique among the others. */
  validateKey = (key: string, rowId: string): string | null => {
    const trimmed: string = key.trim();
    if (!trimmed) return "Please enter a key!";
    if (/\s/.test(trimmed)) return "Field must not contain spaces!";
    const clash: boolean = this.state.rows.some(
      (row: ISessionVariableRow) => row.rowId !== rowId && row.key.trim().toLowerCase() === trimmed.toLowerCase()
    );
    if (clash) return "Key must be unique!";
    return null;
  };

  /**
   * Every cell writes straight to its row, so a variable is written where it will live and nothing has to be added:
   * what the list shows IS what will be saved. Writing does not grow the list; it only restarts the pause that does.
   */
  patchRow = (rowId: string, patch: Partial<ISessionVariable>) => {
    this.setState((previous: ICreateSessionVariableState) => {
      const rows: ISessionVariableRow[] = previous.rows.map((row: ISessionVariableRow) =>
        row.rowId === rowId ? { ...row, ...patch } : row
      );

      return {
        ...previous,
        rows,
        // Editing the key is the correction; the outline goes as soon as it is touched.
        invalidKeyRowIds:
          patch.key === undefined
            ? previous.invalidKeyRowIds
            : previous.invalidKeyRowIds.filter((id: string) => id !== rowId)
      };
    }, this.scheduleGrowTrailingRow);
  };

  /**
   * The empty row waiting under the list, added once the last row is a variable. It used to arrive on the FIRST letter
   * of a value, because growing was part of writing a cell — the list said "done" about something still being typed.
   * It now waits for the typing to stop, and for the three moments that mean the row really is finished: the pause
   * above, leaving a cell, and Enter. All three land here, and the timer is dropped whichever gets here first.
   */
  private growTimer: ReturnType<typeof setTimeout> | undefined = undefined;

  private clearGrowTimer = (): void => {
    if (this.growTimer === undefined) return;

    clearTimeout(this.growTimer);
    this.growTimer = undefined;
  };

  /** Restarts the pause: while the keys keep coming the row below stays away. */
  private scheduleGrowTrailingRow = (): void => {
    this.clearGrowTimer();
    this.growTimer = setTimeout(this.growTrailingRow, GROW_AFTER_TYPING_STOPS_MS);
  };

  growTrailingRow = (): void => {
    this.clearGrowTimer();
    this.setState((previous: ICreateSessionVariableState) => {
      const last: ISessionVariableRow | undefined = previous.rows[previous.rows.length - 1];
      if (!last || !isCompleteRow(last)) return previous;

      return { ...previous, rows: [...previous.rows, blankRow()] };
    });
  };

  /**
   * Checked when the cell is left rather than on every keystroke: a key is not wrong halfway through being typed,
   * and a warning per character would be unreadable. A refused key keeps its cell, outlined.
   */
  checkKey = (row: ISessionVariableRow) => {
    if (isBlankRow(row)) return;
    if (!row.key.trim() && !row.value.trim()) return;

    const error: string | null = this.validateKey(row.key, row.rowId);
    if (!error) {
      this.setState((previous: ICreateSessionVariableState) => ({
        ...previous,
        invalidKeyRowIds: previous.invalidKeyRowIds.filter((id: string) => id !== row.rowId)
      }));
      return;
    }

    KMainFunctions.displayWarningNotification(error);
    this.setState((previous: ICreateSessionVariableState) => ({
      ...previous,
      invalidKeyRowIds: previous.invalidKeyRowIds.includes(row.rowId)
        ? previous.invalidKeyRowIds
        : [...previous.invalidKeyRowIds, row.rowId]
    }));
  };

  getTableColumns = (): KuikaTableColumn<ISessionVariableRow>[] => {
    const rows: ISessionVariableRow[] = this.state.rows;
    // The row being composed is always the last one (see the class doc). Its controls carry a
    // fixed, stable test-id — a row-scoped id would re-address itself every keystroke, since the
    // trailing row has no key yet when it starts being written.
    const trailingRowId: string | undefined = rows[rows.length - 1]?.rowId;

    return [
      {
        id: "key",
        header: () => "Key",
        accessorKey: "key",
        cell: (row: ISessionVariableRow) => {
          const isDraft: boolean = row.rowId === trailingRowId;
          return (
            <KuikaInput
              borderless
              value={row.key}
              placeholder="Enter key.."
              data-nav-id={`${row.rowId}Key`}
              data-testid={isDraft ? "sessionVariableDraftKeyInput" : undefined}
              error={this.state.invalidKeyRowIds.includes(row.rowId)}
              onChange={(_e: unknown, next: string | number): void =>
                this.patchRow(row.rowId, { key: String(next ?? "") })
              }
              onBlur={(): void => {
                this.checkKey(row);
                this.growTrailingRow();
              }}
              // Enter walks the row: key to value, and from a finished value to the empty row waiting below.
              onPressEnter={(): void => this.focusCell(row.rowId, "Value")}
            />
          );
        }
      },
      {
        id: "value",
        header: () => "Value",
        accessorKey: "value",
        cell: (row: ISessionVariableRow) => {
          const isDraft: boolean = row.rowId === trailingRowId;
          // A committed row keeps its value reachable for inline editing (Update flow); it falls
          // back to the row id on the same rare edge the read-only cell below already guards —
          // a key that is blank on a committed row — so two such rows never share one test-id.
          const committedTestId: string = `${row.key || row.rowId}SessionVariableValueInput`;
          return (
            <KuikaInput
              borderless
              value={row.value}
              placeholder="Enter value.."
              data-nav-id={`${row.rowId}Value`}
              data-testid={isDraft ? "sessionVariableDraftValueInput" : committedTestId}
              onChange={(_e: unknown, next: string | number): void =>
                this.patchRow(row.rowId, { value: String(next ?? "") })
              }
              onBlur={(): void => this.growTrailingRow()}
              // Enter finishes the row, so the empty one below has to exist before focus goes looking for it.
              onPressEnter={(): void => {
                this.growTrailingRow();
                requestAnimationFrame(() => this.focusLastRowKey());
              }}
            />
          );
        }
      },
      {
        id: "isReadOnly",
        header: () => "Read Only",
        align: "center",
        accessorFn: (row: ISessionVariableRow) => (row.isReadOnly ? 1 : 0),
        // The state is carried in the test-id rather than a class, so a scenario can
        // assert a specific row's read-only flag without reading the control.
        cell: (row: ISessionVariableRow) => {
          const isDraft: boolean = row.rowId === trailingRowId;
          return (
            <div
              className="kuika-session-variables__switch-cell"
              data-testid={`${row.key || row.rowId}SessionVariableReadOnly${row.isReadOnly ? "On" : "Off"}`}
            >
              <KuikaSwitch
                labelPosition="none"
                checked={row.isReadOnly}
                dataTestId={isDraft ? "sessionVariableDraftReadOnlySwitch" : undefined}
                onChange={(checked: boolean): void => this.patchRow(row.rowId, { isReadOnly: checked })}
              />
            </div>
          );
        }
      },
      {
        id: "actions",
        header: () => "",
        width: 70,
        align: "center",
        // The empty row at the end is not a variable yet, so there is nothing there to remove.
        cell: (row: ISessionVariableRow) =>
          isBlankRow(row) ? null : (
            <div className="kuika_table_databaseDiagram_wrapper__actions-container">
              <Trash12RedSvg
                data-testid={`${row.key}SessionVariableDeleteButton`}
                onClick={() => this.deleteVariable(row)}
              />
            </div>
          )
      }
    ];
  };

  deleteVariable = (row: ISessionVariableRow) => {
    this.setState((previous: ICreateSessionVariableState) => {
      const rows: ISessionVariableRow[] = previous.rows.filter((item: ISessionVariableRow) => item.rowId !== row.rowId);
      if (rows.length === 0 || !isBlankRow(rows[rows.length - 1])) rows.push(blankRow());
      return { ...previous, rows, invalidKeyRowIds: previous.invalidKeyRowIds.filter((id) => id !== row.rowId) };
    });
  };

  setSubmitButtonDisabledStatus() {
    if (this.objectHash === this.getObjectHash()) {
      this.setState({ submitButtonDisabledStatus: true });
      return;
    }

    this.setState({ submitButtonDisabledStatus: false });
  }

  render() {
    this.setSubmitButtonDisabledStatus();
    // The dimming overlay and the close-confirmation dialog ask the same question the button's
    // status answers; they lift while the ✓ is still up, which is when the editor is leaving anyway.
    const isSaving: boolean = this.state.saveStatus === "loading";

    return (
      <KuikaSpinnerSmall spinning={isSaving}>
        <div className="kuika-session-variables" style={{ width: "100%", height: "100%" }}>
          <div className="kuika__data-diagram__create-con kuika__data-diagram__create-con--bar">
            <KuikaEditorHeader
              title="Session Variables"
              backLabel="Back to Datasources"
              // The project has exactly one of these, so the name is an identity with nothing
              // to switch to rather than a switcher chip.
              renderTitle={(title: string) => (
                <KuikaEditorIdentity
                  icon={<EditorGlyphIcon glyph={SESSION_VARIABLES_GLYPH} />}
                  iconTooltip={SESSION_VARIABLES_GLYPH.label}
                  name={title}
                  nameTestId="sessionVariablesTitle"
                />
              )}
              submitLabel="SAVE"
              onSubmit={this.onCreate}
              onCancel={() => this.handleClose()}
              status={this.state.saveStatus}
              isSubmitDisabled={this.state.submitButtonDisabledStatus}
              cancelTestId="cancelButton"
              submitTestId="saveButton"
            />

            <div className="kuika__data-diagram__create-con__container__item kuika-session-variables__list">
              <div className="kuika_table_databaseDiagram_wrapper kuika-session-variables__table" ref={this.tableRef}>
                <KuikaTable<ISessionVariableRow>
                  data-testid="variablesTable"
                  rowKey={(row: ISessionVariableRow) => row.rowId}
                  columns={this.getTableColumns()}
                  // The row being composed is the last row of the list, not a form above it: one place where a
                  // variable is written, and the one being made looks like the ones already made.
                  data={this.state.rows}
                  // The list is as long as the project's variables and no longer; antd showed
                  // them all, and a page control over a handful of rows would be noise.
                  enablePagination={false}
                  // The frame ends where its content does. The kit's 400px floor keeps a
                  // filtered list from collapsing, but this screen has no filter and the floor
                  // left a bordered strip hanging under the last row.
                  fitContentHeight
                />
              </div>
            </div>
          </div>
          <CloseConfirmationModal
            itemName={"Session Variable"}
            closeType={"Session Variable"}
            triggerModal={this.props.showModal}
            isLoading={isSaving}
            updateAndCloseHandler={() => this.onCreate()}
            closeWithoutSaveHandler={() => {
              this.props.setShowModal(false);
              this.close();
            }}
            cancel={() => {
              this.props.setNextCallbackFunction();
              this.props.setShowModal(false);
            }}
          />
        </div>
      </KuikaSpinnerSmall>
    );
  }

  checkNeedRender = (changeReason: ESdChangeReason): boolean => {
    return false;
  };
}

const mapStateToProps = (state: AppState) => ({
  showModal: state.unsavedChangesModal.showModal,
  nextCallback: state.unsavedChangesModal.nextCallback
});

const mapDispatchToProps = (dispatch) => ({
  setShowModal: (showModal: boolean) => dispatch(setShowModal(showModal, null)),
  setNextCallbackFunction: (cb?: () => void) => dispatch(setNextCallbackFunction(cb))
});

const createSessionVariable = connect(
  mapStateToProps,
  mapDispatchToProps
)(withSD<ICreateSessionVariableProps>(CreateSessionVariable));
export { createSessionVariable as CreateSessionVariable };
